// src/app/components/admin/logs-section.tsx
//
// Global Settings → Logs. INFO-and-above application log lines from the
// backend and the celery workers, captured into app_logs by
// backend/common/helper/db_log_handler.py. Newest first, filterable by level,
// source, time window and text; "Load more" pages back with a keyset cursor
// so new lines arriving while you read never shift or duplicate a page.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AlertCircle, ChevronDown, ChevronRight, Loader2, RefreshCcw, ScrollText, Search } from "lucide-react";

import { Button } from "../kit";
import { opsApi, type LogEntry, type LogLevelFilter } from "@/api/ops";
import { formatDateTime, levelColor } from "./health-details-section";

const LEVELS: { id: LogLevelFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "info", label: "Info" },
  { id: "warning", label: "Warnings +" },
  { id: "error", label: "Errors" },
];

const WINDOWS: { hours: number | undefined; label: string }[] = [
  { hours: 1, label: "Last hour" },
  { hours: 24, label: "Last 24 hours" },
  { hours: 24 * 7, label: "Last 7 days" },
  { hours: undefined, label: "Everything kept" },
];

const SOURCES = [
  { id: "", label: "All sources" },
  { id: "backend", label: "Backend API" },
  { id: "celery-worker", label: "Celery workers" },
];

const PAGE_SIZE = 100;
const AUTO_REFRESH_MS = 15_000;

const monoStyle: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: 12,
};

const controlStyle: CSSProperties = {
  padding: "8px 10px",
  borderRadius: 9,
  border: "1px solid var(--border)",
  backgroundColor: "var(--input-background)",
  fontSize: 13,
  color: "var(--foreground)",
  outline: "none",
};

const segBtn = (active: boolean): CSSProperties => ({
  padding: "5px 12px",
  fontSize: 12,
  cursor: "pointer",
  border: "none",
  borderRadius: 7,
  backgroundColor: active ? "var(--alpha-teal-10)" : "transparent",
  color: active ? "var(--alpha-teal)" : "var(--muted-foreground)",
  fontWeight: active ? 600 : 400,
});

function LogRow({ entry }: { entry: LogEntry }) {
  const [open, setOpen] = useState(false);
  const color = levelColor(entry.level);
  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 text-left"
        style={{ padding: "9px 4px", background: "none", border: "none", cursor: "pointer", color: "var(--foreground)" }}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown style={{ width: 14, height: 14, flexShrink: 0, marginTop: 3, color: "var(--muted-foreground)" }} />
        ) : (
          <ChevronRight style={{ width: 14, height: 14, flexShrink: 0, marginTop: 3, color: "var(--muted-foreground)" }} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 11.5, color: "var(--muted-foreground)" }}>
            <span style={{ whiteSpace: "nowrap" }}>{formatDateTime(entry.ts)}</span>
            <span
              style={{
                color,
                border: `1px solid ${color}`,
                borderRadius: 5,
                padding: "0 6px",
                fontWeight: 600,
                fontSize: 10.5,
                letterSpacing: 0.3,
              }}
            >
              {entry.level}
            </span>
            <span>{entry.source}</span>
            <span style={{ ...monoStyle, fontSize: 11, overflowWrap: "anywhere" }}>{entry.logger}</span>
            {entry.exc_text && <span style={{ color: "var(--infrared-coral)" }}>· traceback</span>}
          </div>
          <div
            style={{
              ...monoStyle,
              marginTop: 3,
              whiteSpace: open ? "pre-wrap" : "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              overflowWrap: open ? "anywhere" : undefined,
            }}
          >
            {entry.message}
          </div>
        </div>
      </button>
      {open && (
        <div style={{ padding: "0 4px 12px 26px" }}>
          {entry.exc_text && (
            <pre
              style={{
                ...monoStyle,
                margin: "0 0 10px",
                padding: 12,
                borderRadius: 8,
                backgroundColor: "var(--muted)",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                maxHeight: 360,
                overflowY: "auto",
              }}
            >
              {entry.exc_text}
            </pre>
          )}
          <div className="flex flex-wrap gap-x-5 gap-y-1" style={{ fontSize: 11.5, color: "var(--muted-foreground)" }}>
            {entry.location && <span>at <span style={monoStyle}>{entry.location}</span></span>}
            {entry.host && <span>host <span style={monoStyle}>{entry.host}</span></span>}
            {entry.pid !== null && <span>pid {entry.pid}</span>}
            {entry.sha && <span>build <span style={monoStyle}>{entry.sha}</span></span>}
            {entry.environment && <span>{entry.environment}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export function LogsSection() {
  const [level, setLevel] = useState<LogLevelFilter>("all");
  const [source, setSource] = useState("");
  const [windowIdx, setWindowIdx] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [items, setItems] = useState<LogEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [tablePresent, setTablePresent] = useState(true);
  const [captureOn, setCaptureOn] = useState(true);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  // Drop responses from a superseded query (filters changed mid-flight).
  const requestSeq = useRef(0);

  // Debounce typing into the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const baseQuery = useCallback(
    () => ({
      level,
      source: source || undefined,
      q: search || undefined,
      since_hours: WINDOWS[windowIdx].hours,
      limit: PAGE_SIZE,
    }),
    [level, source, search, windowIdx],
  );

  const load = useCallback(
    async (quiet = false) => {
      const seq = ++requestSeq.current;
      if (!quiet) setLoading(true);
      setErr("");
      try {
        const res = await opsApi.logs(baseQuery());
        if (seq !== requestSeq.current) return;
        setItems(res.items);
        setCursor(res.next_cursor);
        setTablePresent(res.table_present);
        setCaptureOn(res.enabled_in_this_process);
        setRetentionDays(res.retention_days);
      } catch (e: any) {
        if (seq === requestSeq.current) setErr(e?.message || "Failed to load logs");
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [baseQuery],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => load(true), AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const loadMore = async () => {
    if (!cursor) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    try {
      const res = await opsApi.logs({ ...baseQuery(), cursor });
      if (seq !== requestSeq.current) return;
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor);
    } catch (e: any) {
      setErr(e?.message || "Failed to load more logs");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="px-4 md:px-6 py-5 md:py-6">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ScrollText style={{ width: 22, height: 22, color: "var(--signal-blue)" }} />
            <h2
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
                fontWeight: 700,
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              Logs
            </h2>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted-foreground)" }}>
            Info, warning and error lines from the backend API and the celery workers, newest first.
            {retentionDays !== null && <> Kept for {retentionDays} days.</>} Credentials in messages are masked before they are stored.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
          <label className="inline-flex items-center gap-2" style={{ fontSize: 12.5, color: "var(--muted-foreground)", cursor: "pointer" }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <Button
            onClick={() => load()}
            disabled={loading}
            variant="secondary"
            icon={RefreshCcw}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────── */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div
          className="inline-flex"
          role="group"
          aria-label="Level"
          style={{ padding: 3, borderRadius: 9, border: "1px solid var(--border)", backgroundColor: "var(--muted-30)" }}
        >
          {LEVELS.map((l) => (
            <button key={l.id} type="button" onClick={() => setLevel(l.id)} style={segBtn(level === l.id)} aria-pressed={level === l.id}>
              {l.label}
            </button>
          ))}
        </div>
        <select value={source} onChange={(e) => setSource(e.target.value)} style={controlStyle} aria-label="Source">
          {SOURCES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <select value={windowIdx} onChange={(e) => setWindowIdx(Number(e.target.value))} style={controlStyle} aria-label="Time window">
          {WINDOWS.map((w, i) => (
            <option key={w.label} value={i}>
              {w.label}
            </option>
          ))}
        </select>
        <div className="relative" style={{ flex: "1 1 220px", minWidth: 0 }}>
          <Search
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--muted-foreground)" }}
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search message, logger or traceback"
            style={{ ...controlStyle, width: "100%", paddingLeft: 30, boxSizing: "border-box" }}
          />
        </div>
      </div>

      {err && (
        <div
          className="mt-4 p-3 flex items-start gap-2 rounded-lg"
          style={{ backgroundColor: "var(--infrared-coral-5)", border: "1px solid var(--infrared-coral)", fontSize: 13 }}
        >
          <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)", flexShrink: 0, marginTop: 1 }} />
          {err}
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────── */}
      <div
        className="mt-4"
        style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "4px 14px 8px" }}
      >
        {!tablePresent ? (
          <Note>
            The log table doesn't exist on this database yet. Apply <code>backend/db/migrations/2026-10-22_app_logs.sql</code> (
            <code>python -m db.migrate apply 2026-10-22_app_logs.sql --yes</code>); capture starts writing on its own once it exists.
          </Note>
        ) : loading && items.length === 0 ? (
          <Note>
            <Loader2 className="animate-spin inline" style={{ width: 14, height: 14, marginRight: 6 }} />
            Loading…
          </Note>
        ) : items.length === 0 ? (
          <Note>
            No log lines match these filters.
            {!captureOn && " Capture is off in this API process (not a deployed build, or APP_LOG_CAPTURE=0)."}
          </Note>
        ) : (
          <>
            {items.map((entry) => (
              <LogRow key={entry.id} entry={entry} />
            ))}
            <div className="flex items-center justify-between gap-3 flex-wrap" style={{ borderTop: "1px solid var(--border)", paddingTop: 10 }}>
              <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                Showing {items.length.toLocaleString()} line{items.length === 1 ? "" : "s"}
              </span>
              {cursor && (
                <Button variant="secondary" onClick={loadMore} disabled={loadingMore} loading={loadingMore}>
                  Load older
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <div style={{ padding: "18px 4px", fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.6 }}>{children}</div>;
}

export default LogsSection;
